import cn from "classnames"
import { Suspense, lazy, useState } from "react"

import { useEntityAI } from "@/api/useInventory"
import { Alert, Button, Card, Collapse, Empty, Flex, Text } from "@/atoms"
import { useTranslation } from "@/hooks/useTranslation"
import { Heading } from "@/molecules/Heading/Heading"
import { SeverityTag } from "@/molecules/SeverityTag/SeverityTag"
import { getAiAgentRisk } from "@/organisms/Drawers/applications/ai-discovery/useAiDiscovery.select"
import { useCounts } from "@/organisms/Drawers/hooks/useCounts"

import styles from "./AiRiskTabAgentAiDiscoveryDrawer.module.scss"

import type { AgentAiRiskResponse } from "@/api/useInventory.types"
import type { TabComponentProps } from "@/organisms/Drawers/components/TabsDrawerTemplate"

const RiskGraph = lazy(() =>
  import("../RiskGraph").catch(() => ({
    default: () => <div>Failed to load Risk Graph</div>
  }))
)

export const AiRiskTabAgentAiDiscoveryDrawer = (props: TabComponentProps) => {
  const { t } = useTranslation()
  const [activeKeys, setActiveKeys] = useState<Record<string, boolean>>({})
  const recordId = props.record?.id
  const ctxAgent = useEntityAI(recordId, "agent")
  const ctx = useEntityAI<
    AgentAiRiskResponse,
    ReturnType<typeof getAiAgentRisk>
  >(`${recordId}/risks`, "agent", undefined, { select: getAiAgentRisk })
  const { response } = ctx
  const { data } = response || {}
  const collapseClassName = cn(
    "card-agent-ai-discovery-drawer",
    styles.collapse
  )

  const isLoading = ctx.isLoading || ctxAgent.isLoading

  useCounts("aiRisks", ctx, props)

  return (
    <Flex vertical gap={8} className="container-agent-ai-discovery-drawer">
      {!!data && data.length > 0 && (
        <Heading level={5} title={`${data.length} Risks Introduced`} />
      )}
      <Flex vertical gap={24}>
        {isLoading && (
          <Card loading={isLoading} className={collapseClassName} />
        )}
        {!isLoading &&
          data?.map((risk, index) => {
            const id = `${index}_${risk.severity}`
            const collapseId = `${id}_collapse`

            return (
              <Collapse
                destroyOnHidden
                key={id}
                activeKey={activeKeys[collapseId] ? collapseId : undefined}
                className={collapseClassName}
                collapsible="disabled"
                expandIcon={() => null}
                items={[
                  {
                    key: collapseId,
                    label: (
                      <Flex vertical gap={6}>
                        <Flex vertical gap={8}>
                          <Flex align="center" gap={8}>
                            <SeverityTag severity={risk.severity} t={t} />
                            <Text type="secondary">{risk.owaspCategory}</Text>
                          </Flex>
                          <Flex vertical>
                            <Heading level={5} title={risk.title} />
                            <Text>{risk.description}</Text>
                          </Flex>
                        </Flex>
                        <span>
                          {!activeKeys[collapseId] && (
                            <Button
                              type="link"
                              size="small"
                              className={styles.button}
                              onClick={() => {
                                setActiveKeys({
                                  ...activeKeys,
                                  [collapseId]: !activeKeys[collapseId]
                                })
                              }}
                            >
                              More Details
                            </Button>
                          )}
                        </span>
                      </Flex>
                    ),
                    children: (
                      <Flex vertical gap={16}>
                        <Suspense
                          fallback={
                            <Card loading className={styles.cardGraph} />
                          }
                        >
                          <Card className={cn(styles.cardGraph)}>
                            <div className={styles.graph}>
                              <RiskGraph
                                agent={ctxAgent.response?.data}
                                data={risk.graph}
                              />
                            </div>
                          </Card>
                        </Suspense>
                        {!!risk.remediation && (
                          <Alert
                            className={styles.guidance}
                            title={
                              <Text strong className={styles.guidanceTitle}>
                                Remediation Guidance
                              </Text>
                            }
                            description={<Text>{risk.remediation}</Text>}
                          />
                        )}
                        <span>
                          <Button
                            type="link"
                            size="small"
                            className={styles.button}
                            onClick={() => {
                              setActiveKeys({
                                ...activeKeys,
                                [collapseId]: !activeKeys[collapseId]
                              })
                            }}
                          >
                            {activeKeys[collapseId] && "Show Less"}
                          </Button>
                        </span>
                      </Flex>
                    )
                  }
                ]}
              />
            )
          })}
        {!isLoading && data?.length === 0 && (
          <Flex align="center" justify="center">
            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
          </Flex>
        )}
      </Flex>
    </Flex>
  )
}
